import React, { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { TouchableOpacity, StyleSheet, ViewStyle, TouchableWithoutFeedback } from 'react-native';
import { UserRole } from '@alucio/aws-beacon-amplify/src/models';
import { OverflowMenu, MenuItem, Icon } from '@ui-kitten/components';
import { DNABox, DNAText, DNAIcon, DNADivider, DNASlider, Iffy } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAChip, DNAPopover, ProfilePicture } from 'src/components/DNA';
import useScreenNav from 'src/components/DNA/hooks/useScreenNav';
import useCurrentPage from 'src/components/DNA/hooks/useCurrentPage';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import { useCurrentUserORM } from 'src/state/redux/selector/user';
import { ROUTES } from 'src/router/routeDef';
import ProfileScreen from 'src/screens/Profile/Profile';
import { useSearchTextContextProvider } from 'src/components/Header/SearchInput/SearchInput';
import useLogOut, { SOURCE } from 'src/components/Authenticator/LogOut';
import { SignOutStateProvider } from 'src/components/Authenticator/LogOut.proxy';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { useChatButtonSharedResources } from './useChatButtonSharedResources';
import { openKnowledgeBase } from './FAQButton';

const styles = StyleSheet.create({
  profileMenuContainer: {
    minWidth: 280,
    maxWidth: 300,
  },
  profileMenuUserInfoContainer: {
    padding: 16,
    backgroundColor: colors['color-white'],
  },
  profileMenuProfilePicture: {
    alignSelf: 'center',
    justifyContent: 'center',
  },
  profileMenuProfilePicturePopoverText: {
    cursor: 'text',
  } as ViewStyle & { cursor?: string },
  profileMenuUserNameContainer: {
    marginLeft: 12,
  },
  profileMenuUserFilterContainer: {
    marginTop: 16,
  },
  secondaryIcon: {
    width: 8,
    height: 8,
    color: colors['color-danger-500'],
  },
  menuItemContainer: {
    padding: 12,
  },
  menuItemSubLabelContainer: {
    paddingBottom: 8,
  },
})

const USER_ROLES = {
  [UserRole.ALUCIO_ADMIN]: 'Alucio Admin',
  [UserRole.TENANT_PUBLISHER]: 'Publisher',
  [UserRole.TENANT_VIEWER]: 'Viewer',
  [UserRole.TENANT_VIEW_ONLY]: 'View Only',
};

const ROLE_SWITCH = {
  [ROUTES.PUBLISHER_DASHBOARD.UID]: {
    role: USER_ROLES[UserRole.TENANT_VIEWER],
    screen: ROUTES.HOME,
  },
  [ROUTES.EMAIL_TEMPLATES.UID]: {
    role: USER_ROLES[UserRole.TENANT_VIEWER],
    screen: ROUTES.HOME,
  },
  [ROUTES.HUB_TEMPLATES.UID]: {
    role: USER_ROLES[UserRole.TENANT_VIEWER],
    screen: ROUTES.HOME,
  },
  [ROUTES.SEARCH_RESULTS_PUBLISHER.UID]: {
    role: USER_ROLES[UserRole.TENANT_VIEWER],
    screen: ROUTES.HOME,
  },
  [ROUTES.PUBLISHER_FOLDERS.UID]: {
    role: USER_ROLES[UserRole.TENANT_VIEWER],
    screen: ROUTES.HOME,
  },
  [ROUTES.PUBLISHER_SHARED_FOLDERS.UID]: {
    role: USER_ROLES[UserRole.TENANT_VIEWER],
    screen: ROUTES.HOME,
  },
  [ROUTES.PUBLISHER_ARCHIVED_FOLDERS.UID]: {
    role: USER_ROLES[UserRole.TENANT_VIEWER],
    screen: ROUTES.HOME,
  },
  [ROUTES.PUBLISHER_FOLDERS_UPDATES.UID]: {
    role: USER_ROLES[UserRole.TENANT_VIEWER],
    screen: ROUTES.HOME,
  },
  [ROUTES.REPORTS.UID]: {
    role: USER_ROLES[UserRole.TENANT_VIEWER],
    screen: ROUTES.HOME,
  },
  [ROUTES.ADMIN_SETTINGS.UID]: {
    role: USER_ROLES[UserRole.TENANT_VIEWER],
    screen: ROUTES.HOME,
  },
  default: {
    role: USER_ROLES[UserRole.TENANT_PUBLISHER],
    screen: ROUTES.PUBLISHER_DASHBOARD,
  },
}

const analyticsEvents: { [K: string]: [string, Object] } = {
  roleSwitchToMSL: [
    'USER_CHANGE_TO_MSL',
    { action: 'CHANGE_TO_MSL', category: 'USER' },
  ],
  roleSwitchToPublisher: [
    'USER_CHANGE_TO_PUBLISHER',
    { action: 'CHANGE_TO_PUBLISHER', category: 'USER' },
  ],
  logout: [
    'LOGIN_LOGOUT',
    { action: 'LOGOUT', category: 'LOGIN' },
  ],
}

type ProfileMenuContextType = {
  appSettings?: ReturnType<typeof useAppSettings>,
  menu: {
    visible: boolean,
    setVisible: React.Dispatch<React.SetStateAction<boolean>>
  },
  user?: {
    user: ReturnType<typeof useCurrentUserORM>,
    fullName: string,
    labelValues: string[],
    roleSwitch: typeof ROLE_SWITCH['default'],
  },
  nav: {
    currentPage: ReturnType<typeof useCurrentPage>,
    go: ReturnType<typeof useScreenNav>['go'],
    goToProfilePassword: () => void,
    goToLogout: () => void,
  },
  enableDeviceSwitch: boolean,
  profileSlider: {
    profileTabletVisible: boolean,
    setProfileTabletVisible: React.Dispatch<React.SetStateAction<boolean>>,
  }
}

type ProfileMenuActionItemProps = {
  key: string,
  subLabel?: string,
  label: string,
  icon: string,
  secondaryIcon?: string,
  secondaryIconCondition?: boolean,
  condition?: boolean,
  onPress: () => void,
}

const ProfileMenuContext = createContext<ProfileMenuContextType>({} as any)
const currentPageOpts = { exact: false }

const ProfileMenuProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const [visible, setVisible] = useState<boolean>(false)
  const [profileTabletVisible, setProfileTabletVisible] = useState<boolean>(false)
  const currentUserORM = useCurrentUserORM();
  const { go, goTo } = useScreenNav()
  const { signOut } = useLogOut()
  const currentPage = useCurrentPage(currentPageOpts)
  const appSettings = useAppSettings()

  const fullName = currentUserORM?.model?.givenName + ' ' + currentUserORM?.model?.familyName

  /** Default labels from account */
  const filters = currentUserORM?.meta.customFilterValues.defaultFilters ?? []
  const filterDisplayValues = Object.values(filters).map(filters => filters.displayValues).flat()

  const labelValues = Array
    .from(new Set(filterDisplayValues))
    .sort()

  const dismiss = (fn: Function) => (...args) => {
    setVisible(false)
    fn(...args)
  }

  const value = {
    appSettings,
    menu: {
      visible,
      setVisible,
    },
    user: {
      user: currentUserORM,
      fullName,
      labelValues,
      roleSwitch: ROLE_SWITCH[currentPage?.UID ?? ''] ?? ROLE_SWITCH.default,
    },
    nav: {
      currentPage,
      go: dismiss(go),
      goToProfilePassword: dismiss(goTo.PROFILE_PASSWORD),
      goToLogout: dismiss(() => {
        signOut(SOURCE.USER)
        goTo.HOME(undefined, ...analyticsEvents.logout)
      }),
    },
    enableDeviceSwitch: appSettings.isDeviceSwitchEnabled,
    profileSlider: {
      profileTabletVisible,
      setProfileTabletVisible,
    },
  }

  return (
    <ProfileMenuContext.Provider value={value}>
      {children}
    </ProfileMenuContext.Provider>
  )
}

export function useProfileMenu(): ProfileMenuContextType {
  const context = useContext(ProfileMenuContext)

  if (!context) {
    throw new Error('useProfileMenu must be used within the ProfileMenuContextProvider')
  }

  return context;
}

// Menu's Avatar component
const ProfilePictureMenu: React.FC<PropsWithChildren> = (props) => {
  const { deviceMode } = useAppSettings();
  const common = useProfileMenu();

  return (
    <OverflowMenu
      visible={common.menu.visible}
      onBackdropPress={() => common?.menu?.setVisible(false)}
      anchor={() => (
        <TouchableOpacity
          testID="profile-menu"
          activeOpacity={1}
          onPress={() => common?.menu?.setVisible(p => !p)}
        >
          <ProfilePicture
            size={
              deviceMode === 'desktop'
                ? 'ui2Small'
                : 'ui2Large'
            }
          />
        </TouchableOpacity>
      )}
    >
      <MenuItem
        appearance="grouped"
        title={() => props.children as React.ReactElement}
      />
    </OverflowMenu>
  )
}

const ProfileMenuTablet: React.FC = () => {
  const common = useProfileMenu()
  const uselogOutState = useLogOut()

  // Check if user is redirected from account profile route, if so open the account profile slider,
  // and remove the local storage
  const isFromTabletProfile = localStorage.getItem('isFromTabletProfile')
  if (isFromTabletProfile) {
    common?.profileSlider?.setProfileTabletVisible(true)
    localStorage.removeItem('isFromTabletProfile')
  }

  if (!common?.profileSlider?.setProfileTabletVisible) return null

  return (
    <DNASlider
      visible={common?.profileSlider?.profileTabletVisible}
      setVisible={common?.profileSlider?.setProfileTabletVisible}
    >
      <SignOutStateProvider value={uselogOutState}>
        <ProfileScreen />
      </SignOutStateProvider>
    </DNASlider>
  )
}

const ProfileMenuActionItem: React.FC<ProfileMenuActionItemProps> = (props) => {
  const { key, subLabel, label, icon, secondaryIcon, secondaryIconCondition, condition, onPress } = props;
  if (!condition) return null;
  return (
    <DNABox appearance="col">
      <DNADivider />
      <DNABox appearance="col" style={styles.menuItemContainer}>
        <Iffy is={subLabel}>
          <DNABox style={styles.menuItemSubLabelContainer} fill appearance="col">
            <DNAText c2 status="secondary">{subLabel}</DNAText>
          </DNABox>
        </Iffy>
        <TouchableOpacity
          testID={`${key}-menu-option`}
          onPress={onPress}
          activeOpacity={1}
        >
          <DNABox
            alignY="center"
            spacing="sm"
          >
            <DNAIcon.Styled status="tertiary" name={icon} />
            {secondaryIconCondition && <Icon name={secondaryIcon} style={styles.secondaryIcon} />}
            <DNAText>{label}</DNAText>
          </DNABox>
        </TouchableOpacity>
      </DNABox>
    </DNABox>
  )
}

const ProfileMenuUI2: React.FC = () => {
  const common = useProfileMenu();
  const { isOnline, deviceMode } = useAppSettings();
  const { setCurrentSearchString } = useSearchTextContextProvider();
  const isDesktop = deviceMode === 'desktop';
  const featureFlags = useFeatureFlags(
    'enableZendeskChat',
    'enableZendeskKnowledgebase',
    'BEAC_7431_dynamic_zendesk_url',
  );
  const { openChat, unreadMessages } = useChatButtonSharedResources(featureFlags.enableZendeskChat);
  const screenNav = useScreenNav()

  const goToDebug = useCallback(
    () => {
      screenNav.goTo.DEVICE_DEBUG()
    },
    [screenNav.goTo],
  )

  if (!common?.user?.user)
  { return <DNAText>User profile could not be loaded. Please contact an admin</DNAText>; }
  const isPublisher = common?.user.user.model.role === UserRole.TENANT_PUBLISHER
  const PROFILE_MENU_ACTION_ITEMS: ProfileMenuActionItemProps[] = [
    {
      key: 'role-switch',
      subLabel: 'USE BEACON AS ...',
      label: common.user.roleSwitch.role,
      icon: 'user-circle',
      condition: isPublisher && isDesktop,
      onPress: () => {
        if (!common.user?.roleSwitch.screen)
        { return }

        common.nav.go(common.user.roleSwitch.screen)
        setCurrentSearchString('');
      },
    },
    {
      key: 'account-settings',
      label: 'Account settings',
      icon: 'user',
      condition: true,
      onPress: () => {
        if (isDesktop) {
          common?.nav?.goToProfilePassword()
        } else {
          common?.profileSlider?.setProfileTabletVisible(true)
        }
      },
    },
    {
      key: 'help',
      label: 'Help',
      icon: 'question',
      condition: true,
      onPress: async () => {
        if (isOnline && featureFlags.enableZendeskKnowledgebase) {
          const deviceMode = isDesktop ? DeviceMode.desktop : DeviceMode.tablet;
          openKnowledgeBase(featureFlags.BEAC_7431_dynamic_zendesk_url, deviceMode);
        }
        else {
          window.open('/faq', '_blank');
        }
      },
    },
    {
      key: 'chat-support',
      label: 'Chat support',
      icon: 'chat-text',
      secondaryIcon: 'record',
      secondaryIconCondition: unreadMessages,
      condition: featureFlags.enableZendeskChat,
      onPress: openChat,
    },
    {
      key: 'logout',
      label: 'Log out',
      icon: 'sign-out',
      condition: true,
      onPress: common.nav.goToLogout,
    },
    {
      key: 'switch-device-mode',
      label: 'Switch device modes',
      icon: 'devices',
      condition: common.enableDeviceSwitch,
      onPress: () => common.appSettings?.dispatch({ type: 'toggleDeviceMode' }),
    },
  ];

  return (
    <ProfilePictureMenu>
      {/* MAIN INFO */}
      <DNABox appearance="col" style={styles.profileMenuContainer}>
        <DNABox fill appearance="col" style={styles.profileMenuUserInfoContainer}>
          <DNABox>
            <DNAPopover delayPressIn={10} interactive={true}>
              <DNAPopover.Anchor style={styles.profileMenuProfilePicture}>
                <ProfilePicture size={isDesktop ? 'ui2Medium' : 'ui2Large'} />
              </DNAPopover.Anchor>
              <DNAPopover.Content >
                <DNABox>
                  {/* @ts-ignore */}
                  <DNABox fill appearance="col" spacing="sm" style={styles.profileMenuProfilePicturePopoverText}>
                    <DNAText selectable colorMode="dark">
                      User ID: {common.user.user.model.id}
                    </DNAText>
                    <Iffy is={common.user.user.model.tenantId}>
                      <DNAText selectable colorMode="dark">
                        Tenant ID: {common.user.user.model.tenantId}
                      </DNAText>
                    </Iffy>
                  </DNABox>
                </DNABox>
              </DNAPopover.Content>
            </DNAPopover>
            <TouchableWithoutFeedback
              onLongPress={goToDebug}
              delayLongPress={5000}
            >
              <DNABox fill appearance="col" spacing="between" style={styles.profileMenuUserNameContainer}>
                <DNAText h5>{common.user.fullName}</DNAText>
                <DNAText b1>{common.user.user.model.email}</DNAText>
              </DNABox>
            </TouchableWithoutFeedback>
          </DNABox>
          <Iffy is={!!common.user.labelValues.length}>
            <DNABox fill appearance="col" alignX="center" spacing="sm" style={styles.profileMenuUserFilterContainer}>
              <DNABox fill alignX="center" spacing="sm" wrap="start">
                {common.user.labelValues.map((label, idx) => (
                  <DNAChip.Beacon
                    key={`${label}-${idx}`}
                    appearance="filled"
                    children={label}
                    style={{ marginBottom: 4 }}
                  />
                ))}
              </DNABox>
            </DNABox>
          </Iffy>
        </DNABox>
        <DNABox appearance="col">
          {PROFILE_MENU_ACTION_ITEMS.map((item) => {
            if (!item.condition) return null;
            return <ProfileMenuActionItem {...item} />;
          })}
        </DNABox>
      </DNABox>
    </ProfilePictureMenu>
  )
}

const ProfileMenu: React.FC = () => {
  const { deviceMode } = useAppSettings();
  const isTablet = deviceMode === 'tablet';
  return (
    <ProfileMenuProvider>
      <ProfileMenuUI2 />
      {isTablet && <ProfileMenuTablet />}
    </ProfileMenuProvider>
  )
}

export default ProfileMenu
